import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import uploadImage from '@/images/uploadIcon.svg';
import { PolotnoStoreActivePageKeys, usePolotnoActivePageState, useSetPolotnoActivePageState } from '@/pages/AdvancedEditor/hooks';
import { CacheStatus } from '@/pages/AdvancedEditor/hooks/useUploadTtsCache';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import theme from '@/theme';
import { annoynmousUserToEditorPagePostSsoReturnTo } from '@/utils/utils';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { Card } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

const UploadAudioControlContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const UploadBox = styled(Card, { shouldForwardProp: prop => prop !== 'isDragActive' && prop !== 'isLoading' })<{
    isDragActive?: boolean;
    isLoading?: boolean;
    error?: boolean;
}>`
    display: flex;
    width: 100%;
    height: 200px;
    border-radius: 12px;
    cursor: pointer;
    align-items: center;
    justify-content: center;

    border: 1px solid ${({ theme, isDragActive }) => (isDragActive ? theme.colors.primary[900] : theme.colors.black[300])};
    margin-block-start: 8px;

    ${({ isLoading, isDragActive }) =>
        !(isLoading || isDragActive) &&
        `
        border: 0;
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23DBDBDBFF' stroke-width='3' stroke-dasharray='6%2c 14' stroke-dashoffset='7' stroke-linecap='square'/%3e%3c/svg%3e");    
        `}

    ${({ error }) =>
        error &&
        `
        border: 0;
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23ED254E' stroke-width='3' stroke-dasharray='6%2c 14' stroke-dashoffset='7' stroke-linecap='square'/%3e%3c/svg%3e");
    `}

    box-shadow:
        0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.11),
        0.5px 1.3px 1.8px -0.6px rgba(187, 187, 187, 0.18),
        1.1px 2.7px 3.8px -1.2px rgba(187, 187, 187, 0.26);
`;

const Dropzone = styled(UploadBox)<{ isDragActive?: boolean }>`
    cursor: pointer;
    outline: none !important;
    transition: background linear 0.3s;
    border: none;
    background: ${({ theme, isDragActive }) => (isDragActive ? `${theme.colors.primary.bgLight} !important` : 'none')};
`;

const UploadText = styled.span`
    font-size: 1.3rem;
`;

const Loading = styled(Player)`
    height: 8rem;
    width: 8rem;
`;

const UploadBoxContent = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 24px 18px;
    flex-direction: column;
`;

const MainText = styled.span`
    font-size: 20px;
    color: ${({ theme }) => theme.colors.black.main};
    display: flex;
    flex-direction: column;
    align-items: center;
    font-weight: bold;
    margin: 1rem 2rem;
`;

const SubText = styled.span`
    font-size: 14px;
    text-align: center;
`;

const DropTextContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ErrorMessage = styled.span`
    font-size: 15px;
    color: ${({ theme }) => theme.colors.error[650]};
`;

interface Props<T> {
    accept: DropzoneOptions['accept'];
    onInputChange: (file: File) => Promise<T>;
    isUploading: boolean;
    setIsUploading: (isUploading: boolean) => void;
    onUploadComplete?: () => void;
}
const UploadAudioControl = observer(<T,>({ accept, onInputChange, isUploading, setIsUploading, onUploadComplete }: Props<T>) => {
    const { isAnonymous, loginWithRedirect } = useAuth();
    const setTtsAudioCacheStatus = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsAudioCacheStatus);
    const [ttsAudioError, setTtsAudioError] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsAudioError);
    const mixpanel = useAnalytics();

    const handleDrop = async (acceptedFiles: File[]) => {
        setTtsAudioError(null);

        setIsUploading(true);
        setTtsAudioCacheStatus(CacheStatus.Invalid);
        try {
            await onInputChange(acceptedFiles[0]);
        } catch (e) {
            logger.error('Error while uploading audio', { e });
        }
        onUploadComplete?.();
        setIsUploading(false);
    };

    const handleAnonymous = async () => {
        mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-box', isAnonymous });
        await loginWithRedirect({ appState: { returnTo: annoynmousUserToEditorPagePostSsoReturnTo } });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop,
        accept,
        multiple: false,
    });

    if (isUploading) {
        return (
            <UploadBox isLoading>
                <Loading src={LoadingAnimation} autoplay loop />
                <UploadText>Uploading...</UploadText>
            </UploadBox>
        );
    }

    return (
        <UploadAudioControlContainer>
            <UploadBox isDragActive={isDragActive} error={!!ttsAudioError}>
                <Dropzone {...getRootProps()} {...(isAnonymous ? { onClick: handleAnonymous } : {})} isDragActive={isDragActive}>
                    <input data-testid="upload-audio-input" {...getInputProps()} {...(isAnonymous ? { onClick: handleAnonymous } : {})} />
                    {isDragActive ? (
                        <DropTextContainer>
                            <MainText>Drag to upload</MainText>
                        </DropTextContainer>
                    ) : (
                        <UploadBoxContent>
                            <MaskedIcon src={uploadImage} size="32px" color={theme.colors.secondary[500]} />

                            <MainText id="upload-audio">Upload audio</MainText>
                            <SubText>Click or drag here. </SubText>
                            <SubText>MP3/MP4/WAV. Up to 15MB</SubText>
                        </UploadBoxContent>
                    )}
                </Dropzone>
            </UploadBox>
            {ttsAudioError && <ErrorMessage>{ttsAudioError}</ErrorMessage>}
        </UploadAudioControlContainer>
    );
});

export default UploadAudioControl;
